import kyWithSentry from './helper/kyWithSentry';
import merge from 'lodash.merge';

import getShortFormVideoTrendURL from 'url-constructors/ShortFormVideo/getShortFormVideoTrendUrl';

import { IShortFormVideoTrendUrlProps } from 'url-constructors/ShortFormVideo/getShortFormVideoTrendUrl';

const getSpecificRequest =
  (props: IShortFormVideoTrendUrlProps): (() => Promise<any>) =>
  (): Promise<any> =>
    kyWithSentry.get(getShortFormVideoTrendURL(props)).json();

const addContentType = (trends: any, contentType: string): any => {
  if (!trends || trends.length === 0) return;
  return trends.map((trend: any) => {
    const data = trend.data.map((trend: any) => ({ ...trend, contentType }));
    return { ...trend, data };
  });
};

/**
 * Initially shortFormVideo data had only one type of data (pgc) and that was why we did not need to set the type in url to send a request to server.
 * Now the data has two types - pgc and ugc. So we need to set them in the server requests.
 */

const shortFormVideoTrendInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData?: any
): Promise<Response> => {
  const {
    compareMode,
    selectedContentType,
    selectedMetricType,
    totalContentType,
  } = interceptionData;

  const pgcViewsProps = merge(
    {},
    interceptionData,
    { metricType: 'views' },
    !totalContentType && { contentType: 'pgc' }
  );
  const pgcCreationsProps = merge(
    {},
    interceptionData,
    { metricType: 'creations' },
    !totalContentType && { contentType: 'pgc' }
  );
  const ugcViewsProps = {
    ...interceptionData,
    metricType: 'views',
    contentType: 'ugc',
  };
  const ugcCreationsProps = {
    ...interceptionData,
    metricType: 'creations',
    contentType: 'ugc',
  };

  const pgcViewsUrl = getSpecificRequest(pgcViewsProps);
  const pgcCreationsUrl = getSpecificRequest(pgcCreationsProps);
  const ugcViewsUrl = getSpecificRequest(ugcViewsProps);
  const ugcCreationsUrl = getSpecificRequest(ugcCreationsProps);

  const shortFormVideoTrendPGCViewsFetch = async (): Promise<any> =>
    ((totalContentType && compareMode && selectedMetricType === 'views') ||
      (totalContentType && !compareMode) ||
      (!totalContentType && selectedMetricType === 'ugc-pgc') ||
      (selectedMetricType === 'views' &&
        compareMode &&
        selectedContentType === 'pgc')) &&
    pgcViewsUrl();

  const shortFormVideoTrendPGCCreationsFetch = async (): Promise<any> =>
    ((totalContentType && compareMode && selectedMetricType === 'creations') ||
      (totalContentType && !compareMode) ||
      (!totalContentType && selectedMetricType === 'ugc-pgc') ||
      (selectedMetricType === 'creations' &&
        compareMode &&
        selectedContentType === 'pgc')) &&
    pgcCreationsUrl();

  const shortFormVideoTrendUGCViewsFetch = async (): Promise<any> =>
    ((!totalContentType && selectedMetricType === 'ugc-pgc') ||
      (selectedContentType === 'ugc' && selectedMetricType === 'views')) &&
    ugcViewsUrl();

  const shortFormVideoTrendUGCCreationsFetch = async (): Promise<any> =>
    ((!totalContentType && selectedMetricType === 'ugc-pgc') ||
      (selectedContentType === 'ugc' && selectedMetricType === 'creations')) &&
    ugcCreationsUrl();

  try {
    const promises: Promise<any>[] = [
      shortFormVideoTrendPGCViewsFetch(),
      shortFormVideoTrendPGCCreationsFetch(),
      shortFormVideoTrendUGCViewsFetch(),
      shortFormVideoTrendUGCCreationsFetch(),
    ];

    const [
      shortFormVideoPGCViewsData,
      shortFormVideoPGCCreationsData,
      shortFormVideoUGCViewsData,
      shortFormVideoUGCCreationsData,
    ] = await Promise.all(promises);

    return new Response(
      JSON.stringify({
        shortFormVideoPGCViewsData: addContentType(
          shortFormVideoPGCViewsData,
          totalContentType ? 'views' : 'pgc-views'
        ),
        shortFormVideoPGCCreationsData: addContentType(
          shortFormVideoPGCCreationsData,
          totalContentType ? 'creations' : 'pgc-creations'
        ),
        ...(shortFormVideoUGCViewsData && {
          shortFormVideoUGCViewsData: addContentType(
            shortFormVideoUGCViewsData,
            totalContentType ? 'views' : 'ugc-views'
          ),
        }),
        ...(shortFormVideoUGCCreationsData && {
          shortFormVideoUGCCreationsData: addContentType(
            shortFormVideoUGCCreationsData,
            totalContentType ? 'creations' : 'ugc-creations'
          ),
        }),
        pgcViewsUrl: getShortFormVideoTrendURL(pgcViewsProps),
        pgcCreationsUrl: getShortFormVideoTrendURL(pgcCreationsProps),
        ugcViewsUrl: getShortFormVideoTrendURL(ugcViewsProps),
        ugcCreationsUrl: getShortFormVideoTrendURL(ugcCreationsProps),
      })
    );
  } catch (error) {
    const errorInfo = await error.response.json();

    return new Response(
      JSON.stringify({
        reason: {
          error: errorInfo.message,
          errorInfo,
        },
      })
    );
  }
};

export default shortFormVideoTrendInterceptor;
